import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../database/firebase";
import { motion, AnimatePresence } from "framer-motion";
import Body from "@mjcdev/react-body-highlighter";
import {
  ArrowLeft,
  Scale,
  Ruler,
  TrendingUp,
  TrendingDown,
  Activity,
  Heart,
  Target,
  Edit3,
  Save,
  Plus,
  X,
  AlertCircle,
  Zap,
  Info,
} from "lucide-react";
import toast from "react-hot-toast";

const OurBody = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [wellnessData, setWellnessData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editMeasurements, setEditMeasurements] = useState({});
  const [loading, setLoading] = useState(true);
  const [bodyData, setBodyData] = useState([]);
  const [painPoints, setPainPoints] = useState([]);
  const [showPainModal, setShowPainModal] = useState(false);
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [newPainPoint, setNewPainPoint] = useState({
    bodyPart: "",
    severity: "medium",
    notes: "",
    startDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (user?.uid) {
      loadWellnessData();
    }
  }, [user]);

  const loadWellnessData = async () => {
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setWellnessData(data.wellnessProfile);

        // Load pain points
        setPainPoints(data.healthInfo?.painPoints || []);

        // Initialize edit measurements
        if (data.wellnessProfile?.bodyMetrics?.current) {
          setEditMeasurements({
            weight: data.wellnessProfile.bodyMetrics.current.weight || 0,
            height: data.wellnessProfile.bodyMetrics.current.height || data.height || 0,
            waist: data.wellnessProfile.bodyMetrics.current.waist || 0,
            hip: data.wellnessProfile.bodyMetrics.current.hip || 0,
            chest: data.wellnessProfile.bodyMetrics.current.chest || 0,
          });
        }

        // Convert pain points to body highlighter format
        const painData = (data.healthInfo?.painPoints || []).map((pain) => ({
          slug: pain.bodyPart,
          intensity: pain.severity === "high" ? 3 : pain.severity === "medium" ? 2 : 1,
        }));
        setBodyData(painData);
      }
    } catch (error) {
      console.error("Error loading wellness data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMeasurements = async () => {
    try {
      const userRef = doc(db, "users", user.uid);
      const height = parseFloat(editMeasurements.height) || wellnessData?.bodyMetrics?.current?.height || 170;
      const newBMI = (editMeasurements.weight / ((height / 100) ** 2)).toFixed(1);

      const today = new Date().toISOString().split("T")[0];
      const newHistoryEntry = {
        date: today,
        weight: parseFloat(editMeasurements.weight),
        bmi: parseFloat(newBMI),
        measurements: {
          height: parseFloat(editMeasurements.height) || null,
          waist: parseFloat(editMeasurements.waist) || null,
          hip: parseFloat(editMeasurements.hip) || null,
          chest: parseFloat(editMeasurements.chest) || null,
        },
      };

      const existingHistory = wellnessData?.bodyMetrics?.history || [];
      const updatedHistory = [...existingHistory, newHistoryEntry];

      await updateDoc(userRef, {
        "wellnessProfile.bodyMetrics.current": {
          ...wellnessData.bodyMetrics.current,
          weight: parseFloat(editMeasurements.weight),
          height: parseFloat(editMeasurements.height) || wellnessData.bodyMetrics.current.height,
          bmi: parseFloat(newBMI),
          waist: parseFloat(editMeasurements.waist) || null,
          hip: parseFloat(editMeasurements.hip) || null,
          chest: parseFloat(editMeasurements.chest) || null,
        },
        height: parseFloat(editMeasurements.height) || wellnessData.bodyMetrics.current.height,
        "wellnessProfile.bodyMetrics.history": updatedHistory,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Measurements updated!");
      setIsEditMode(false);
      loadWellnessData();
    } catch (error) {
      console.error("Error saving measurements:", error);
      toast.error("Failed to save measurements");
    }
  };

  const handleBodyPartClick = (bodyPart) => {
    setSelectedBodyPart(bodyPart);
    setNewPainPoint({
      ...newPainPoint,
      bodyPart: bodyPart.slug || bodyPart,
    });
    setShowPainModal(true);
  };

  const handleAddPainPoint = async () => {
    if (!newPainPoint.bodyPart) {
      toast.error("Please select a body part");
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingPainPoints = userData.healthInfo?.painPoints || [];
      const updatedPainPoints = [
        ...existingPainPoints,
        {
          ...newPainPoint,
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
        },
      ];

      await updateDoc(userRef, {
        "healthInfo.painPoints": updatedPainPoints,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Pain point added!");
      setShowPainModal(false);
      setNewPainPoint({
        bodyPart: "",
        severity: "medium",
        notes: "",
        startDate: new Date().toISOString().split("T")[0],
      });
      loadWellnessData();
    } catch (error) {
      console.error("Error adding pain point:", error);
      toast.error("Failed to add pain point");
    }
  };

  const handleDeletePainPoint = async (painId) => {
    try {
      const userRef = doc(db, "users", user.uid);
      const updatedPainPoints = painPoints.filter((p) => p.id !== painId);

      await updateDoc(userRef, {
        "healthInfo.painPoints": updatedPainPoints,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Pain point removed");
      loadWellnessData();
    } catch (error) {
      console.error("Error deleting pain point:", error);
      toast.error("Failed to remove pain point");
    }
  };

  const getBodyTypeColor = (bodyType) => {
    switch (bodyType) {
      case "Underweight": return "#60a5fa";
      case "Healthy": return "#34d399";
      case "Overweight": return "#fbbf24";
      case "Obese": return "#f87171";
      default: return "#9ca3af";
    }
  };

  const getBodyTypeIcon = (bodyType) => {
    switch (bodyType) {
      case "Underweight": return "📉";
      case "Healthy": return "✅";
      case "Overweight": return "📊";
      case "Obese": return "⚠️";
      default: return "📊";
    }
  };

  const MetricCard = ({ icon: Icon, label, value, unit, color, trend, editable, editKey }) => (
    <div className="bg-gray-50 rounded-3xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <span className="text-sm text-gray-600 font-medium">{label}</span>
      </div>
      {isEditMode && editable ? (
        <div className="flex items-baseline gap-2">
          <input
            type="number"
            value={editMeasurements[editKey] || ""}
            onChange={(e) => setEditMeasurements({ ...editMeasurements, [editKey]: e.target.value })}
            className="w-24 px-3 py-2 bg-white border-2 border-[#CDFF00] rounded-xl text-2xl font-bold text-gray-900 outline-none"
            step="0.1"
          />
          <span className="text-sm text-gray-500">{unit}</span>
        </div>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900">{value}</span>
          <span className="text-sm text-gray-500">{unit}</span>
        </div>
      )}
      {trend && !isEditMode && (
        <div className={`flex items-center gap-1 mt-2 ${trend > 0 ? "text-green-600" : "text-red-600"}`}>
          {trend > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span className="text-xs font-medium">{Math.abs(trend)}% this month</span>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CDFF00] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading body metrics...</p>
        </div>
      </div>
    );
  }

  if (!wellnessData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Complete Your Wellness Profile
          </h2>
          <p className="text-gray-500 mb-6">
            Track your body metrics and progress
          </p>
          <button
            onClick={() => navigate("/onboarding")}
            className="px-8 py-4 bg-[#CDFF00] text-black rounded-2xl font-bold text-lg"
          >
            Start Onboarding
          </button>
        </div>
      </div>
    );
  }

  const { bodyMetrics, bodyAnalysis, personalInfo } = wellnessData;
  const currentWeight = bodyMetrics?.current?.weight || 0;
  const targetWeight = bodyMetrics?.target?.weight || 0;
  const weightDiff = currentWeight - targetWeight;
  const bmi = bodyMetrics?.current?.bmi || 0;
  const bodyType = bodyAnalysis?.bodyType || "Unknown";
  const height = bodyMetrics?.current?.height || 0;
  const waist = bodyMetrics?.current?.waist || 0;
  const hip = bodyMetrics?.current?.hip || 0;
  const chest = bodyMetrics?.current?.chest || 0;
  const gender = personalInfo?.gender?.toLowerCase() || "male";

  const history = bodyMetrics?.history || [];
  const lastMonthWeight = history.length > 0 ? history[history.length - 1]?.weight : currentWeight;
  const weightTrend = lastMonthWeight ? (((currentWeight - lastMonthWeight) / lastMonthWeight) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Status Bar */}
      <div className="h-12 flex items-center justify-center px-6">
        <div className="text-sm font-semibold">9:41</div>
      </div>

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-black">Body Metrics</h1>
              <p className="text-sm text-gray-500">Track your physical progress</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (isEditMode) {
                handleSaveMeasurements();
              } else {
                setIsEditMode(true);
              }
            }}
            className="w-10 h-10 rounded-full bg-[#CDFF00] flex items-center justify-center hover:bg-[#b8e600] transition-colors"
          >
            {isEditMode ? (
              <Save className="w-5 h-5 text-black" />
            ) : (
              <Edit3 className="w-5 h-5 text-black" />
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: "overview", label: "Overview", icon: Activity },
            { id: "body", label: "Body Map", icon: Zap },
            { id: "measurements", label: "Measurements", icon: Ruler },
            { id: "progress", label: "Progress", icon: TrendingUp },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all ${activeTab === tab.id
                    ? "bg-[#CDFF00] text-black"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Body Type Card */}
            <div
              className="rounded-3xl p-6 text-white"
              style={{ background: `linear-gradient(135deg, ${getBodyTypeColor(bodyType)}, ${getBodyTypeColor(bodyType)}dd)` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm opacity-90 mb-1">Body Type</p>
                  <h2 className="text-3xl font-bold flex items-center gap-2">
                    {getBodyTypeIcon(bodyType)} {bodyType}
                  </h2>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90 mb-1">BMI</p>
                  <p className="text-3xl font-bold">{bmi}</p>
                </div>
              </div>
              <p className="text-sm opacity-90">
                {bodyType === "Healthy" && "You're in a healthy weight range! Keep it up!"}
                {bodyType === "Underweight" && "Focus on nutrient-dense foods to reach a healthy weight."}
                {bodyType === "Overweight" && "Small changes in diet and activity can make a big difference."}
                {bodyType === "Obese" && "Consult with a healthcare provider for a personalized plan."}
              </p>
            </div>

            {/* Key Metrics */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Key Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  icon={Scale}
                  label="Current Weight"
                  value={currentWeight}
                  unit="kg"
                  color="#6366f1"
                  trend={parseFloat(weightTrend)}
                  editable
                  editKey="weight"
                />
                <MetricCard
                  icon={Target}
                  label="Target Weight"
                  value={targetWeight}
                  unit="kg"
                  color="#10b981"
                />
                <MetricCard
                  icon={Ruler}
                  label="Height"
                  value={height}
                  unit="cm"
                  color="#f59e0b"
                  editable
                  editKey="height"
                />
                <MetricCard
                  icon={Activity}
                  label="To {weightDiff > 0 ? 'Lose' : 'Gain'}"
                  value={Math.abs(weightDiff).toFixed(1)}
                  unit="kg"
                  color={weightDiff > 0 ? "#ef4444" : "#3b82f6"}
                />
              </div>
            </div>

            {/* Goal Timeline */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Target className="w-6 h-6 text-purple-600" />
                <h3 className="font-bold text-gray-900">Your Goal</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-2">
                {wellnessData.goals?.primary || "Not set"}
              </p>
              <p className="text-sm text-gray-600">
                Timeline: {bodyMetrics?.target?.timeline || "Not set"}
              </p>
            </div>
          </div>
        )}

        {/* Body Map Tab */}
        {activeTab === "body" && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Pain & Injury Map</h3>
                <button
                  onClick={() => setShowPainModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#CDFF00] rounded-xl text-black text-sm font-medium hover:bg-[#b8e600] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Pain Point
                </button>
              </div>

              {/* Body Visualizations */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Front View */}
                <div className="bg-white rounded-2xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 text-center">Front View</h4>
                  <div className="flex justify-center">
                    <Body
                      data={bodyData}
                      gender={gender}
                      side="front"
                      scale={1.5}
                      onClick={handleBodyPartClick}
                    />
                  </div>
                </div>

                {/* Back View */}
                <div className="bg-white rounded-2xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 text-center">Back View</h4>
                  <div className="flex justify-center">
                    <Body
                      data={bodyData}
                      gender={gender}
                      side="back"
                      scale={1.5}
                      onClick={handleBodyPartClick}
                    />
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 justify-center text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-yellow-400"></div>
                  <span>Low Pain</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                  <span>Medium Pain</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-600"></div>
                  <span>High Pain</span>
                </div>
              </div>

              {/* Pain Points List */}
              {painPoints.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Active Pain Points</h4>
                  <div className="space-y-3">
                    {painPoints.map((pain) => (
                      <div key={pain.id} className="bg-white rounded-2xl p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-900 capitalize">
                                {pain.bodyPart.replace(/-/g, " ")}
                              </span>
                              <span
                                className={`px-2 py-1 text-xs rounded-full font-medium ${pain.severity === "high"
                                    ? "bg-red-100 text-red-700"
                                    : pain.severity === "medium"
                                      ? "bg-orange-100 text-orange-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                              >
                                {pain.severity}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Started: {new Date(pain.startDate).toLocaleDateString()}
                            </p>
                            {pain.notes && (
                              <p className="text-sm text-gray-600 mt-2">{pain.notes}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeletePainPoint(pain.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {painPoints.length === 0 && (
                <div className="text-center py-8">
                  <Info className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">No pain points recorded</p>
                  <p className="text-sm text-gray-400">
                    Click on the body or add a pain point to track injuries
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Measurements Tab */}
        {activeTab === "measurements" && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Body Measurements</h3>
              <div className="grid grid-cols-2 gap-4">
                <MetricCard
                  icon={Ruler}
                  label="Waist"
                  value={waist || "Not set"}
                  unit="cm"
                  color="#ec4899"
                  editable
                  editKey="waist"
                />
                <MetricCard
                  icon={Ruler}
                  label="Hip"
                  value={hip || "Not set"}
                  unit="cm"
                  color="#8b5cf6"
                  editable
                  editKey="hip"
                />
                <MetricCard
                  icon={Ruler}
                  label="Chest"
                  value={chest || "Not set"}
                  unit="cm"
                  color="#06b6d4"
                  editable
                  editKey="chest"
                />
                <div className="bg-gray-50 rounded-3xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-5 h-5 text-red-500" />
                    <span className="text-sm text-gray-600 font-medium">WHR</span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">
                    {waist && hip ? (waist / hip).toFixed(2) : "N/A"}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Waist-to-Hip Ratio</p>
                </div>
              </div>
            </div>

            {/* Measurement Tips */}
            <div className="bg-blue-50 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Measurement Tips</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Measure at the same time each day</li>
                    <li>• Use a flexible measuring tape</li>
                    <li>• Waist: Measure at belly button level</li>
                    <li>• Hip: Measure at widest point</li>
                    <li>• Chest: Measure at nipple level</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Tab */}
        {activeTab === "progress" && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Weight History</h3>
              {history.length > 0 ? (
                <div className="bg-gray-50 rounded-3xl p-6">
                  {/* Simple bar chart */}
                  <div className="h-48 flex items-end gap-2 mb-4">
                    {history.slice(-10).map((entry, index) => {
                      const maxWeight = Math.max(...history.map(h => h.weight));
                      const height = (entry.weight / maxWeight) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center gap-1">
                          <div
                            className="w-full bg-gradient-to-t from-[#CDFF00] to-[#a8cc00] rounded-t-lg transition-all"
                            style={{ height: `${height}%` }}
                          />
                          <span className="text-xs text-gray-500 rotate-45 origin-left">
                            {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Latest entries */}
                  <div className="space-y-2">
                    {history.slice(-5).reverse().map((entry, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded-xl">
                        <span className="text-sm text-gray-600">
                          {new Date(entry.date).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-gray-900">{entry.weight} kg</span>
                          <span className="text-sm text-gray-500">BMI: {entry.bmi}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-3xl p-12 text-center">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No history yet</p>
                  <p className="text-sm text-gray-400">
                    Update your measurements to start tracking progress
                  </p>
                </div>
              )}
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-2xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Entries</p>
                <p className="text-2xl font-bold text-gray-900">{history.length}</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Avg Weight</p>
                <p className="text-2xl font-bold text-gray-900">
                  {history.length > 0
                    ? (history.reduce((sum, h) => sum + h.weight, 0) / history.length).toFixed(1)
                    : currentWeight}
                </p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Change</p>
                <p className={`text-2xl font-bold ${weightTrend > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {weightTrend > 0 ? '+' : ''}{weightTrend}%
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pain Point Modal */}
      <AnimatePresence>
        {showPainModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPainModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-md w-full"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Add Pain Point</h3>
                <button
                  onClick={() => setShowPainModal(false)}
                  className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Body Part Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Body Part
                  </label>
                  <select
                    value={newPainPoint.bodyPart}
                    onChange={(e) =>
                      setNewPainPoint({
                        ...newPainPoint,
                        bodyPart: e.target.value,
                      })
                    }
                    className="w-full bg-gray-50 text-gray-900 rounded-xl px-4 py-3 border-2 border-gray-200 focus:border-[#CDFF00] outline-none"
                  >
                    <option value="">Select body part</option>
                    <option value="head">Head</option>
                    <option value="neck">Neck</option>
                    <option value="shoulders">Shoulders</option>
                    <option value="upper-back">Upper Back</option>
                    <option value="lower-back">Lower Back</option>
                    <option value="chest">Chest</option>
                    <option value="abs">Abs</option>
                    <option value="left-arm">Left Arm</option>
                    <option value="right-arm">Right Arm</option>
                    <option value="left-knee">Left Knee</option>
                    <option value="right-knee">Right Knee</option>
                    <option value="left-ankle">Left Ankle</option>
                    <option value="right-ankle">Right Ankle</option>
                  </select>
                </div>

                {/* Severity */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Severity
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {["low", "medium", "high"].map((severity) => (
                      <button
                        key={severity}
                        onClick={() =>
                          setNewPainPoint({ ...newPainPoint, severity })
                        }
                        className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${newPainPoint.severity === severity
                            ? severity === "high"
                              ? "bg-red-500 text-white"
                              : severity === "medium"
                                ? "bg-orange-500 text-white"
                                : "bg-yellow-400 text-black"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {severity.charAt(0).toUpperCase() + severity.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newPainPoint.startDate}
                    onChange={(e) =>
                      setNewPainPoint({
                        ...newPainPoint,
                        startDate: e.target.value,
                      })
                    }
                    className="w-full bg-gray-50 text-gray-900 rounded-xl px-4 py-3 border-2 border-gray-200 focus:border-[#CDFF00] outline-none"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={newPainPoint.notes}
                    onChange={(e) =>
                      setNewPainPoint({
                        ...newPainPoint,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Describe the pain, triggers, etc."
                    rows={3}
                    className="w-full bg-gray-50 text-gray-900 rounded-xl px-4 py-3 border-2 border-gray-200 focus:border-[#CDFF00] outline-none resize-none"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowPainModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddPainPoint}
                    className="flex-1 px-4 py-3 bg-[#CDFF00] text-black rounded-xl font-semibold hover:bg-[#b8e600] transition-colors"
                  >
                    Add Pain Point
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Indicator */}
      <div className="fixed bottom-0 left-0 right-0 h-8 bg-white flex items-center justify-center">
        <div className="w-32 h-1 bg-black rounded-full"></div>
      </div>
    </div>
  );
};

export default OurBody;
